import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { useToast } from '@/components/ui/use-toast';
import {
  projectsApi,
  ProjectFilters,
  UpdateBillingStatusPayload,
  BillingHistoryFilters,
  AnalysisRunProgress,
} from '@/api/projects';
import { getErrorMessage } from '@/utils/errorUtils';

const RUNNING_PHASES: AnalysisRunProgress['phase'][] = [
  'per_question_pages',
  'meta_synthesis',
  'overall',
];

export function isAnalysisRunning(progress?: AnalysisRunProgress): boolean {
  return !!progress && RUNNING_PHASES.includes(progress.phase);
}

/**
 * Query key factory for projects
 */
export const projectKeys = {
  all: ['projects'] as const,
  lists: () => [...projectKeys.all, 'list'] as const,
  list: (filters: ProjectFilters) => [...projectKeys.lists(), filters] as const,
  details: () => [...projectKeys.all, 'detail'] as const,
  detail: (id: string) => [...projectKeys.details(), id] as const,
  billing: (id: string) => [...projectKeys.detail(id), 'billing'] as const,
  billingHistory: (id: string, filters?: BillingHistoryFilters) =>
    [...projectKeys.billing(id), 'history', filters] as const,
  analysisRunProgress: (id: string) =>
    [...projectKeys.detail(id), 'analysis-run-progress'] as const,
  filters: (id: string) => [...projectKeys.detail(id), 'filters'] as const,
};

/**
 * Hook to fetch paginated list of projects
 */
export function useProjects(filters: ProjectFilters) {
  return useQuery({
    queryKey: projectKeys.list(filters),
    queryFn: () => projectsApi.getAll(filters),
    placeholderData: keepPreviousData,
    staleTime: 30000, // 30 seconds
  });
}

// ==================== Billing Status ====================

/**
 * Hook to fetch project with billing details
 */
export function useProjectWithBilling(id: string) {
  return useQuery({
    queryKey: projectKeys.billing(id),
    queryFn: () => projectsApi.getWithBilling(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook to fetch project billing history
 */
export function useProjectBillingHistory(id: string, filters?: BillingHistoryFilters) {
  return useQuery({
    queryKey: projectKeys.billingHistory(id, filters),
    queryFn: () => projectsApi.getBillingHistory(id, filters),
    enabled: !!id,
    staleTime: 30000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Hook to restore a soft-deleted project
 */
export function useRestoreProject() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      organizationId,
      workspaceId,
    }: {
      id: string;
      organizationId: string;
      workspaceId: string;
    }) => projectsApi.restore(id, organizationId, workspaceId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.billing(variables.id) });
      toast({
        title: 'Success',
        description: 'Project restored successfully',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to restore project'),
        variant: 'destructive',
      });
    },
  });
}

// ==================== Analysis Re-run ====================

/**
 * Polls analysis pipeline progress every 5 seconds while the project page is
 * open. Mirrors the dashboard's useAnalysisRunProgress convention: an
 * unconditional interval (the analyzer caches this response in Redis, so
 * polling is cheap) with `refetchIntervalInBackground` so a multi-minute run
 * keeps advancing even when the admin switches tabs.
 *
 * A conditional interval that returns `false` when idle was tried first, but
 * it froze the UI mid-run — once the timer is cleared it does not re-arm when
 * the run starts, so the card stuck on the first "Running" frame. The fixed
 * interval is the proven pattern.
 */
export function useAnalysisRunProgress(id: string) {
  return useQuery({
    queryKey: projectKeys.analysisRunProgress(id),
    queryFn: () => projectsApi.getAnalysisRunProgress(id),
    enabled: !!id,
    refetchInterval: 5000,
    refetchIntervalInBackground: true,
    refetchOnWindowFocus: true,
    staleTime: 0,
    retry: 0,
  });
}

/**
 * Hook to list a project's report filters, for the single-filter re-run
 * scope picker (TELV1-500). Empty array (not an error) when the project has
 * no filters — the picker degrades to "Main analysis" only.
 *
 * `enabled` defaults to true, but the re-run dialog passes `open` so the
 * request only fires once the dialog is actually opened — otherwise every
 * project page view would issue a GET that's only ever used inside the
 * dialog's scope picker.
 */
export function useProjectFilters(id: string, enabled = true) {
  return useQuery({
    queryKey: projectKeys.filters(id),
    queryFn: () => projectsApi.listFilters(id),
    enabled: !!id && enabled,
    staleTime: 30000,
  });
}

export interface ForceRerunAnalysisVariables {
  id: string;
  /** Omit for the default main/unfiltered re-run. */
  filterId?: string;
  /** Display-only — names the scope in the success toast. Not sent to the API. */
  filterName?: string;
}

/**
 * Hook to force a full analysis re-run — either the default main/unfiltered
 * analysis, or (TELV1-500) a single selected filter via `filterId`. On
 * trigger, invalidates the progress query so the poller picks the run up
 * immediately.
 */
export function useForceRerunAnalysis() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, filterId }: ForceRerunAnalysisVariables) =>
      projectsApi.forceRerunAnalysis(id, filterId),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.analysisRunProgress(variables.id) });
      const scopeLabel = variables.filterName ?? 'Main analysis (unfiltered)';
      if (data.state === 'loading') {
        toast({
          title: 'Analysis re-run started',
          description: `Re-run started for ${scopeLabel}. This takes a few minutes.`,
        });
      } else {
        // `done` without a run means the analyzer had nothing to analyze
        // (e.g. no digested conversations yet).
        toast({
          title: 'Nothing to analyze',
          description: 'This project has no completed conversations to run an analysis on.',
        });
      }
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to start the analysis re-run'),
        variant: 'destructive',
      });
    },
  });
}

/**
 * Hook to update project billing status
 */
export function useUpdateBillingStatus() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: UpdateBillingStatusPayload }) =>
      projectsApi.updateBillingStatus(id, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: projectKeys.lists() });
      queryClient.invalidateQueries({ queryKey: projectKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: projectKeys.billing(variables.id) });
      // billingStatus may be absent (other fields updated) or explicit null
      // ("New") under the now-partial payload — only mention it when set.
      const description = variables.payload.billingStatus
        ? `Billing status updated to ${variables.payload.billingStatus}`
        : 'Billing details updated';
      toast({
        title: 'Success',
        description,
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to update billing status'),
        variant: 'destructive',
      });
    },
  });
}
